import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { useDebugMode } from '@cobliteam/hooks/src/useDebugMode';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { memo, useCallback, useMemo, useState } from 'react';
import { useOpenRouterModels } from '../hooks/useOpenRouterModels';
import { DEBUG_MODELS, getModelProvider, toMastraModel } from '../lib/debugModels';
import { useAIAssistant } from '../store';
import {
  ModelSelector,
  ModelSelectorContent,
  ModelSelectorEmpty,
  ModelSelectorGroup,
  ModelSelectorInput,
  ModelSelectorItem,
  ModelSelectorList,
  ModelSelectorLogo,
  ModelSelectorName,
  ModelSelectorTrigger,
} from './elements/ModelSeletor';

type ModelOption = { value: string; label: string; provider?: string };

const stripPrefix = (value: string) => value.replace(/^openrouter\//, '');

/** When not searching, only preview a slice of the (large) catalog to keep the list snappy. */
const CATALOG_PREVIEW_LIMIT = 50;

const ModelDebugSelector = () => {
  const { t } = useTranslation('ai');
  const { environment } = useDashboard();
  const [isDebug] = useDebugMode();
  // Shown for dev builds, or anywhere (incl. production) when debug mode is enabled.
  const isEnabled = Boolean(environment?.isDev) || isDebug;

  const { selectedModel, setSelectedModel } = useAIAssistant();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Catalog is fetched only once the palette is opened.
  const { data: catalog = [], isLoading, isError } = useOpenRouterModels({ enabled: open && isEnabled });

  const curatedOptions = useMemo<ModelOption[]>(() => DEBUG_MODELS.map((value) => ({ value, label: stripPrefix(value), provider: getModelProvider(value) })), []);

  const catalogOptions = useMemo<ModelOption[]>(() => {
    const curatedValues = new Set<string>(DEBUG_MODELS);
    const deduped = catalog.filter((option) => !curatedValues.has(option.value));
    return search.trim() ? deduped : deduped.slice(0, CATALOG_PREVIEW_LIMIT);
  }, [catalog, search]);

  const customValue = toMastraModel(search.trim());
  const hasExactMatch = useMemo(() => [...curatedOptions, ...catalog].some((option) => option.value === customValue), [curatedOptions, catalog, customValue]);
  const showCustom = search.trim().length > 0 && !hasExactMatch;

  const handleSelect = useCallback(
    (value: string | null) => {
      setSelectedModel(value);
      setOpen(false);
      setSearch('');
    },
    [setSelectedModel]
  );

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) setSearch('');
  }, []);

  if (!isEnabled) return null;

  return (
    <ModelSelector open={open} onOpenChange={handleOpenChange}>
      <ModelSelectorTrigger asChild>
        <Button
          variant='ghost'
          size='sm'
          aria-label={t('model_selector.aria_label')}
          startIcon={<Icon name='cpu' className='size-3.5' />}
          className={cn('h-7 max-w-[180px] gap-1 px-2 font-mono text-xs', selectedModel ? 'text-primary-11' : 'text-gray-9')}
        >
          <span className='truncate'>{selectedModel ? stripPrefix(selectedModel) : t('model_selector.default')}</span>
        </Button>
      </ModelSelectorTrigger>

      <ModelSelectorContent title={t('model_selector.title')}>
        <ModelSelectorInput value={search} onValueChange={setSearch} placeholder={t('model_selector.search_placeholder')} />

        <ModelSelectorList>
          <ModelSelectorEmpty>{t('model_selector.empty')}</ModelSelectorEmpty>

          {/* Reset to backend default */}
          <ModelSelectorGroup>
            <ModelSelectorItem value='__default__' keywords={['padrão', 'default', 'reset']} onSelect={() => handleSelect(null)}>
              <Icon name='rotate-ccw' className='size-4 text-gray-9' />
              <ModelSelectorName>{t('model_selector.use_default')}</ModelSelectorName>
              {!selectedModel && <Icon name='check' className='size-4 text-primary-9' />}
            </ModelSelectorItem>
          </ModelSelectorGroup>

          {/* Free-text: use the typed string verbatim */}
          {showCustom && (
            <ModelSelectorGroup heading={t('model_selector.custom_heading')}>
              <ModelSelectorItem value={`custom:${customValue}`} forceMount onSelect={() => handleSelect(customValue)}>
                <Icon name='pencil' className='size-4 text-gray-9' />
                <ModelSelectorName>{t('model_selector.use_custom', { model: customValue })}</ModelSelectorName>
              </ModelSelectorItem>
            </ModelSelectorGroup>
          )}

          <ModelSelectorGroup heading={t('model_selector.top_heading')}>
            {curatedOptions.map((option) => (
              <ModelSelectorItem key={option.value} value={option.value} keywords={[option.label]} onSelect={() => handleSelect(option.value)}>
                {option.provider && <ModelSelectorLogo provider={option.provider} onError={(event) => event.currentTarget.classList.add('invisible')} />}
                <ModelSelectorName>{option.label}</ModelSelectorName>
                {selectedModel === option.value && <Icon name='check' className='size-4 text-primary-9' />}
              </ModelSelectorItem>
            ))}
          </ModelSelectorGroup>

          <ModelSelectorGroup heading={t('model_selector.all_heading')}>
            {isLoading && (
              <ModelSelectorItem value='__loading__' disabled>
                <Icon name='loader' className='size-4 animate-spin text-gray-9' />
                <ModelSelectorName>{t('model_selector.loading')}</ModelSelectorName>
              </ModelSelectorItem>
            )}
            {isError && !isLoading && (
              <ModelSelectorItem value='__error__' disabled>
                <Icon name='circle-alert' className='size-4 text-gray-9' />
                <ModelSelectorName>{t('model_selector.load_error')}</ModelSelectorName>
              </ModelSelectorItem>
            )}
            {catalogOptions.map((option) => (
              <ModelSelectorItem key={option.value} value={option.value} keywords={[option.label]} onSelect={() => handleSelect(option.value)}>
                <ModelSelectorName className='flex min-w-0 flex-col'>
                  <span className='truncate'>{option.label}</span>
                  <span className='truncate font-mono text-[10px] text-gray-9'>{stripPrefix(option.value)}</span>
                </ModelSelectorName>
                {selectedModel === option.value && <Icon name='check' className='size-4 shrink-0 text-primary-9' />}
              </ModelSelectorItem>
            ))}
          </ModelSelectorGroup>
        </ModelSelectorList>
      </ModelSelectorContent>
    </ModelSelector>
  );
};

export default memo(ModelDebugSelector);
